import React, { useState, useEffect, useContext } from "react";
import PopoverInput from "./inner/popoverInput.jsx";
import PickerFooter from "./inner/pickerFooter.jsx";
import { DataSourceContext, IsMobileContext } from "../context/contexts.jsx";
import { onActivateKeyDown, FOCUS_RING } from "../helpers/a11y.js";

const icon = <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.00024 20H18.0002M3.8252 12.1661C3.5502 11.9661 3.3002 11.7161 3.00021 10.8411C2.91884 10.6241 2.61132 9.53619 2.35021 8.54109C2.12996 7.7017 1.9337 6.93555 2.02521 6.74109C2.10022 6.54109 2.2002 6.39109 2.5252 6.19109C2.7252 6.06802 3.7502 5.81609 3.9502 5.76609C4.1502 5.71609 4.42519 5.69109 4.6502 5.76609C5.0752 5.84109 5.9502 7.11609 6.1752 7.26609C6.27519 7.36609 6.6002 7.657 6.9752 7.69109C7.2502 7.71609 7.5252 7.64109 7.82521 7.51609C8.1002 7.40151 13.5252 4.76609 14.0252 4.54109C18.1002 2.84109 21.0602 5.63609 21.5102 6.23609C21.9752 6.81609 22.0752 6.99109 21.9502 7.49109C21.7886 8.01609 21.3502 8.11609 21.1002 8.19109C20.8502 8.26609 17.4002 9.19109 16.0502 9.56609C15.7553 9.6621 15.6113 9.85492 15.5752 9.89109C15.4002 10.1411 14.6052 11.8411 14.3802 12.2161C14.2252 12.6161 13.8002 13.1161 13.2502 13.3161C12.6752 13.5161 11.6752 13.7411 11.4502 13.8161C11.2252 13.8911 10.7002 14.0411 10.5252 13.9911C10.3002 13.9411 10.0852 13.7161 10.1852 13.3661C10.2852 13.0161 10.4752 12.0411 10.5002 11.8911C10.5252 11.7411 10.7752 11.1161 10.5002 11.0911C10.4502 11.0161 9.9252 11.2411 9.1502 11.4161C8.57442 11.5782 7.97143 11.7386 7.5502 11.8411C5.9252 12.3161 5.04514 12.4411 4.8502 12.4411C4.4752 12.4411 4.2002 12.3911 3.8252 12.1661Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"></path>
</svg>;
// Small plane shown next to each airport row, tilted slightly so it reads as
// "taking off" rather than pointing straight up.
const planeIcon = (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" style={{ transform: "rotate(45deg)" }}>
        <path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5L21 16z" />
    </svg>
);
// Small tick rendered inside the checkbox; transparent (so the box reads as
// empty) until the row is selected.
const tickIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 6L9 17L4 12" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);
const searchIcon = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 21l-4.3-4.3M11 19a8 8 0 1 1 0-16 8 8 0 0 1 0 16Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Row styled to match the destination picker: a clickable row that highlights
// and shows a tick when selected, rather than a native checkbox.
const SelectItem = ({ label, isSelected, onToggle, bold }) => {
    return (
        <div onClick={onToggle} role="checkbox" tabIndex={0} aria-checked={isSelected} aria-label={label}
            onKeyDown={onActivateKeyDown(onToggle)}
            className={"flex items-center gap-3 rounded-[8px] mr-2 p-2 cursor-pointer " + FOCUS_RING + " " +
                (isSelected ? "bg-ocean-100 text-primary" : "text-content_2 hover:bg-content_5")}>
            <span className={"shrink-0 w-5 h-5 rounded-[5px] border-2 flex items-center justify-center transition-colors " +
                (isSelected ? "bg-emp border-emp text-white" : "border-content_4 text-transparent")}>
                {tickIcon}
            </span>
            <span className="shrink-0 text-content_3">{planeIcon}</span>
            <span className={"flex-1 " + (bold ? "font-semibold" : "")}>{label}</span>
        </div>
    );
}

const ALL_LONDON = ["LGW", "STN", "LCY", "LHR", "LTN"];

const DeparturePicker = ({ selectedAirports, onUpdate, updateOverviewText, error }) => {
    const [availableDepAirs, setAvailableDepAirs] = useState(null);
    const [filterText, setFilterText] = useState("");
    const [isOpen, setIsOpen] = useState(false);
    const dataSource = useContext(DataSourceContext);
    const isMobile = useContext(IsMobileContext);

    useEffect(() => {
        async function fetchData() {
            setAvailableDepAirs(null)
            var departureAirports = await dataSource.getDepartures();
            setAvailableDepAirs(departureAirports);
        }
        fetchData();
    }, []);
    useEffect(() => {
        updateOverviewText(buildDepartureText());
    }, [selectedAirports, availableDepAirs]);

    // Clear the search text on close so the field shows the selection summary.
    useEffect(() => {
        if (!isOpen) {
            setFilterText("");
        }
    }, [isOpen]);


    const onDepartureChange = (code, isSelected) => {
        if (isSelected) {
            onUpdate([...selectedAirports, code]);
        } else {
            var newAirportList = selectedAirports.filter((item) => item !== code);
            onUpdate(newAirportList);
        }
    }
    const buildDepartureText = () => {
        let inputText = "";
        //HasLoaded
        if (availableDepAirs) {

            var verifiedSelectedAir = [];
            for (let index = 0; index < selectedAirports.length; index++) {
                const myCode = selectedAirports[index];
                var found = availableDepAirs.find((item) => item.code == myCode);
                if (found) {
                    verifiedSelectedAir.push(found.name);
                }
            }

            if (verifiedSelectedAir.length == 1) {
                inputText = verifiedSelectedAir[0];
            } else if (verifiedSelectedAir.length > 1) {

                inputText = verifiedSelectedAir[0] + " +" + (verifiedSelectedAir.length - 1);

            } else {
                inputText = "Departing From";
            }

        } else {
            inputText = "Loading...";
        } return inputText;

    }

    const toggleAirports = (codes) => {
        var validCodes = [];
        if (areAllValidSelected(codes)) {
            //unselect
            validCodes = selectedAirports.filter((x) => !codes.includes(x));
        }
        else {
            var currentSelected = [...selectedAirports, ...codes.filter((x) => availableDepAirs.find((item) => item.code == x) || selectedAirports.includes(x))];
            currentSelected = [...new Set(currentSelected)];
            validCodes = currentSelected;
        }
        onUpdate(validCodes);
        updateOverviewText(buildDepartureText())
    }
    const areAllValidSelected = (codes) => {
        if (availableDepAirs) {
            var validCodes = codes.filter((x) => availableDepAirs.find((item) => item.code == x));

            return validCodes.every(element => selectedAirports.includes(element));
        }
        return false;
    }
    let filteredAirports = [];

    let inputText = "Departure Airport";
    if (availableDepAirs) {

        inputText = buildDepartureText();
        if (filterText) {
            filteredAirports = availableDepAirs.filter((item) => item.name.toLowerCase().includes(filterText.toLowerCase()) || item.code.toLowerCase() == filterText.toLowerCase());
        } else {
            filteredAirports = availableDepAirs;
        }
    }

    // Mobile search box (desktop types into the field itself).
    let header = null;
    if (isMobile) {
        header = <div className="relative">
            <input type="text" value={filterText} onChange={(e) => setFilterText(e.target.value)} className="form-input form-input--with-icon" placeholder={"Search airports"} />
            <span className="absolute left-3 top-[20px] -translate-y-1/2 pointer-events-none text-content_2">
                {searchIcon}
            </span>
        </div>;
    }

    return <PopoverInput error={error} doubleWidth={false} header={header} footer={<PickerFooter onClear={() => onUpdate([])} onConfirm={() => setIsOpen(false)} clearDisabled={selectedAirports.length == 0} />} icon={icon} inputText={inputText} isLoading={availableDepAirs == null} onTextUpdate={setFilterText} filterText={filterText} isOpen={isOpen} setIsOpen={setIsOpen}>
        <div className="grid grid-cols-1">
            {!filterText && availableDepAirs?.length > 0 && (
                <SelectItem label="All London" bold isSelected={areAllValidSelected(ALL_LONDON)} onToggle={() => toggleAirports(ALL_LONDON)} />
            )}
            {filteredAirports?.map((x, i) => {
                return <SelectItem key={i} label={x.name} isSelected={selectedAirports.includes(x.code)} onToggle={() => onDepartureChange(x.code, !selectedAirports.includes(x.code))} />
            })}
        </div>
        {/* Loaded, but nothing to show: distinguish "no airports at all" from
            "no match for the current search" so a failed/empty fetch doesn't
            look like a broken, empty panel. */}
        {availableDepAirs && availableDepAirs.length == 0 && (
            <div className="text-content_2 text-sm p-3 text-center">No departure airports available.</div>
        )}
        {availableDepAirs && availableDepAirs.length > 0 && filterText && filteredAirports.length == 0 && (
            <div className="text-content_2 text-sm p-3 text-center">No airports match &ldquo;{filterText}&rdquo;.</div>
        )}
    </PopoverInput>
}
export default DeparturePicker;
